import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { FileText, TrendingUp, TrendingDown, RefreshCw, ArrowRightLeft, Edit3 } from 'lucide-react';
import { format } from 'date-fns';

interface LedgerEvent {
  id: string;
  company_id: string;
  shareholder_id: string;
  event_type: 'ISSUANCE' | 'TRANSFER' | 'CANCELLATION' | 'SAFE_CONVERSION' | 'ADJUSTMENT';
  shares: number;
  share_class: string;
  price_per_share: number | null;
  note_reference: string | null;
  created_at: string;
  created_by: string;
}

interface LedgerEventListProps {
  events: LedgerEvent[];
  loading: boolean;
}

const getEventIcon = (eventType: string) => {
  switch (eventType) {
    case 'ISSUANCE':
      return <TrendingUp className="h-4 w-4" />;
    case 'TRANSFER':
      return <ArrowRightLeft className="h-4 w-4" />;
    case 'CANCELLATION':
      return <TrendingDown className="h-4 w-4" />;
    case 'SAFE_CONVERSION':
      return <RefreshCw className="h-4 w-4" />;
    case 'ADJUSTMENT':
      return <Edit3 className="h-4 w-4" />;
    default:
      return <FileText className="h-4 w-4" />;
  }
};

const getEventColor = (eventType: string) => {
  switch (eventType) {
    case 'ISSUANCE':
      return 'default';
    case 'TRANSFER':
      return 'secondary';
    case 'CANCELLATION':
      return 'destructive';
    case 'SAFE_CONVERSION':
      return 'outline';
    case 'ADJUSTMENT':
      return 'outline';
    default:
      return 'default';
  }
};

export const LedgerEventList = ({ events, loading }: LedgerEventListProps) => {
  if (loading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-3/4" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-10">
          <FileText className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No ledger events recorded yet</p>
          <p className="text-sm text-muted-foreground">Click "Record Event" to get started</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {events.map((event) => (
        <Card key={event.id} className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                {getEventIcon(event.event_type)}
                <Badge variant={getEventColor(event.event_type) as any}>{event.event_type}</Badge>
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Shares:</span>
                <span className="font-semibold">{event.shares.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Class:</span>
                <span>{event.share_class}</span>
              </div>
              {event.price_per_share && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Price:</span>
                  <span>${event.price_per_share.toFixed(2)}</span>
                </div>
              )}
              {event.note_reference && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Note:</span>
                  <span className="text-xs truncate max-w-[150px]">{event.note_reference}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t">
                <span className="text-muted-foreground">Date:</span>
                <span className="text-xs">{format(new Date(event.created_at), 'MMM d, yyyy')}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
